import { constant, pages } from '@/config'

const DEFAULT = {
  headers: {
    'Content-Type': 'application/json'
  }
}

function _getHeaders (headers) {
  return {
    ...DEFAULT.headers,
    ...headers
  }
}

function _responseAdapter (res) {
  return res.json().then((body) => {
    const newRes = {
      ...res,
      ok: res.ok,
      url: res.url,
      status: res.status,
      body: {
        ...body,
        httpStatus: res.status
      }
    }
    if (!res.ok) {
      throw newRes.body || newRes
    }
    return newRes
  })
}

async function _blobResponseAdapter (res) {
  if (res.headers.get('content-type') !== 'application/octet-stream' &&
    res.headers.get('content-type') !== 'application/json' &&
    res.headers.get('content-type') !== 'application/pdf'
  ) {
    throw res
  }
  if (!res.ok) {
    const body = await res.json()
    const newRes = {
      ...body,
      httpStatus: res.status
    }
    throw newRes
  }

  return res
}

const _emptyValues = [undefined, null, '']
function _hasValue (value) {
  if (_emptyValues.includes(value)) {
    return false
  }
  if (Array.isArray(value)) {
    return value.filter(i => !_emptyValues.includes(i))
      .length
  }
  return true
}

function safeRedirect (path) {
  if (window.location.pathname === path) return
  window.location.replace(path)
}

export function defaultHandlerConnectionError (error, auth) {
  const errorMessage = error.errors?.errorMessage?.[0]
  switch (error.httpStatus) {
    case 401: {
      if (auth) {
        safeRedirect(pages.home.path)
      }
      break
    }
    case 403: {
      if (errorMessage === constant.UNVERIFIED_STATUS) {
        safeRedirect(pages.unverified.path)
      } else {
        safeRedirect(pages.rejected.path)
      }
      break
    }
    default:
  }
}

function buildUrl ({
  url,
  queryParams
}) {
  const isFullUrl = url.indexOf('http') === 0
  const urlObj = new URL((isFullUrl ? '' : document.location.origin) + url)
  const searchParams = urlObj.searchParams
  Object.entries(queryParams || {})
    .filter(([_, value]) => _hasValue(value))
    .forEach(([key, value]) => {
      const values = Array.isArray(value) ? value : [value]
      values.forEach(i => searchParams.append(key, i))
    })
  return urlObj
}

export function getDataViaApi ({
  url,
  queryParams,
  headers,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'GET',
    headers: _getHeaders(headers)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function getDataViaApiBlob ({
  url,
  queryParams,
  headers,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'GET',
    headers: _getHeaders(headers)
  }).then(_blobResponseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function postDataViaApi ({
  url,
  queryParams,
  headers,
  body,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'POST',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function postDataViaApiBlob ({
  url,
  headers,
  body,
  auth = true
}) {
  const urlObj = buildUrl({ url })
  return fetch(urlObj, {
    method: 'POST',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_blobResponseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function postDataViaApiFormData ({
  url,
  body,
  auth = true
}) {
  const urlObj = buildUrl({ url })
  return fetch(urlObj, {
    method: 'POST',
    body
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function putDataViaApi ({
  url,
  queryParams,
  headers,
  body,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'PUT',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function deleteDataViaApi ({
  url,
  queryParams,
  headers,
  body,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'DELETE',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export function requestDataViaExternalApi ({
  url,
  method,
  queryParams,
  headers,
  body,
  auth = true
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method,
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error, auth)
      throw error
    })
}

export default {
  getDataViaApi,
  getDataViaApiBlob,
  postDataViaApi,
  postDataViaApiBlob,
  postDataViaApiFormData,
  putDataViaApi,
  deleteDataViaApi,
  requestDataViaExternalApi
}
